// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the commits VCA collection.
 *
 * Git commits from any connected provider. One row per unique (gitConnectionId, repoId, sha).
 *
 * Identity tuple: (gitConnectionId, repoId, sha)
 * ID prefix: cmt_
 */

import { z } from 'zod'

export const commitsSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Git connection reference ──────────────────────────────────────────
  gitConnectionId: z.string().max(100),
  codebaseId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  repoId: z.string().max(200),
  /** Full 40-char SHA or abbreviated SHA (stored as received from provider). */
  sha: z.string().max(100),

  // ── Commit content ────────────────────────────────────────────────────
  /** Full commit message including body. */
  message: z.string(),

  // ── Author / committer ────────────────────────────────────────────────
  /** Provider login of the author. Null for unlinked authors (no account). */
  authorLogin: z.string().max(200).nullable().optional(),
  /** Email from the git commit metadata. */
  authorEmail: z.string().max(200),

  // ── Timestamps ───────────────────────────────────────────────────────
  /** When the author wrote the commit (git author date). */
  authoredAt: z.string().datetime(),
  /** When the committer applied the commit (git committer date). */
  committedAt: z.string().datetime(),

  // ── Graph ────────────────────────────────────────────────────────────
  /** SHAs of immediate parent commits (empty for root commits). */
  parentShas: z.array(z.string()).optional(),

  // ── Provider-specific extras ──────────────────────────────────────────
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type CommitsInput = z.infer<typeof commitsSchema>
